import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    VerificationStepsEnum,
    PhoneNumberComponent as PhoneNumber,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    FormFooterComponent as FormFooter,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    MarketConsentWrapper,

    // Types
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    StudentPersonalInfoResponse,
} from '@sheerid/jslib';
import { LogoHeaderComponent } from './LogoHeaderComponent';
import { BirthDateComponent as BirthDate } from './BirthDate2Component'


interface StepStudentPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

export const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentPersonalViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    return (
        <div id="sid-step-active-student-professional-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <LogoHeaderComponent/>
                    <div className="sid-header__title sid-l-space-top-lg">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name, Email & Organization" />
                    </div>
                    <div className="sid-header__subtitle sid-l-space-top-sm sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name, organization name, and email must match what is on record with your agency." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <LogoHeaderComponent/>
                    <div className="sid-header__title sid-l-space-top-lg">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-space-top-sm">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a student." />
                    </div>
                    <div className="sid-header__how-verifying-works">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateStudentPersonalViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateStudentPersonalViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue) => {
                        updateStudentPersonalViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateStudentPersonalViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue) => {
                                updateStudentPersonalViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyStudentPersonalStatus" defaultMessage="Verify My Student Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="StudentPersonalInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooter>
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
